/**
 * Canonical email validation for SpotMe forms.
 *
 * Stricter than the legacy `/^\S+@\S+$/i` we used across most lead forms —
 * that one accepts `juan@gmail`, `juan@`, `@gmail.com`, etc., and lets a
 * verification email get queued for an address that can never receive it,
 * stranding the user mid-flow.
 *
 * The pattern requires at least one `.` after the `@`, no spaces, and a
 * non-empty localpart, domain, and TLD. It deliberately does NOT try to be
 * RFC 5321-perfect (that's a several-hundred-character regex and the real
 * authority on whether an email works is the verification email itself).
 * This is "block the obvious typos" pre-submit validation.
 *
 * Used by every email field in the demand + KYC + dashboard forms. Do not
 * fork — if a new edge case needs a different shape, update this regex and
 * re-verify the existing callers, don't introduce a parallel one.
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Imperative validator for code paths that don't use react-hook-form's
 * `rules.pattern` (e.g. inline form submit guards, on-demand WhatsApp
 * fallback flows). Returns false on null/undefined/empty.
 */
export const isValidEmail = (value: string | null | undefined): boolean => {
  if (!value) return false
  return EMAIL_REGEX.test(value.trim())
}

/**
 * Domain-typo suggester for email inputs ("hotmai.com" → "hotmail.com").
 *
 * Two real users went email-dark from one mistyped address before this
 * existed (spotme-server docs/email-bounce-suppression-gap.md →
 * "Spam-report lockout"): the address hard-bounces, the suppression store
 * marks it dead, and with no phone on file nobody can ever reach them again.
 * The input is the only cheap point of intervention. This covers the DOMAIN
 * half only — a local-part typo (schelin@ for schellin@) is a valid address
 * that simply is not theirs, and no client check can see that. Render it via
 * `src/components/Inputs/EmailTypoHint`.
 */
/** Providers actually seen in the MX user base, most common first.
 *  MIRRORED in spotme-server `src/api/notification/emailRepairParse.ts` (the
 *  WhatsApp email repair) — change both, or the bot and the form propose
 *  different corrections for the same typo. */
const COMMON_DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'yahoo.com.mx',
  'icloud.com',
  'live.com',
  'live.com.mx',
  'hotmail.es',
  'outlook.es',
  'prodigy.net.mx',
  'protonmail.com'
]

const KNOWN_DOMAINS = new Set(COMMON_DOMAINS)

/** Max edits for a suggestion. 1 for short domains, 2 for longer ones —
 *  "gmail.com" → "gmial.com" is 2 edits, "live.com" → "lime.com" should NOT
 *  match at 2 (a quarter of the string). */
const maxDistanceFor = (domain: string): number => (domain.length >= 9 ? 2 : 1)

/** Plain Levenshtein with an early-out band; inputs are tiny (≤ ~30 chars). */
const editDistance = (a: string, b: string, cap: number): number => {
  if (Math.abs(a.length - b.length) > cap) return cap + 1
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i]
    for (let j = 1; j <= b.length; j += 1) {
      row[j] = Math.min(
        prev[j] + 1,
        row[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      )
    }
    prev = row
  }
  return prev[b.length]
}

/**
 * The corrected full address when the domain looks like a typo of a common
 * provider, else null. Never fires on an exact known domain, so corporate and
 * uncommon-but-real domains are left alone unless they sit within edit
 * distance of a big provider.
 */
export const suggestEmailFix = (email: string): string | null => {
  const trimmed = email.trim().toLowerCase()
  const at = trimmed.lastIndexOf('@')
  if (at <= 0 || at === trimmed.length - 1) return null

  const local = trimmed.slice(0, at)
  const domain = trimmed.slice(at + 1)
  if (KNOWN_DOMAINS.has(domain)) return null
  // Too short to diagnose, or still being typed (no dot yet).
  if (domain.length < 5 || !domain.includes('.')) return null

  let best: string | null = null
  let bestDistance = Number.MAX_SAFE_INTEGER
  for (const candidate of COMMON_DOMAINS) {
    const cap = maxDistanceFor(candidate)
    const distance = editDistance(domain, candidate, cap)
    if (distance <= cap && distance < bestDistance) {
      best = candidate
      bestDistance = distance
    }
  }
  return best ? `${local}@${best}` : null
}
